import { useState } from "react";
import { useCountdown } from "shared/model";
import { getErrorStatusCode } from "shared/utils";
import type { AuthResponse } from "entities/user";
import { useVerifyTelegramCodeMutation } from "../api/twoFactorVerifyApi";
import { getVerifyError } from "./getVerifyError";

const CODE_EXPIRED_STATUS = 410;

interface UseTelegramVerifyResult {
  code: string;
  setCode: (code: string) => void;
  submit: () => void;
  isSubmitting: boolean;
  errorMessage: string | null;
  isExpired: boolean;
  secondsLeft: number;
}

export const useTelegramVerify = (
  challengeId: string,
  expiresAt: string,
  onSuccess: (result: AuthResponse) => void,
): UseTelegramVerifyResult => {
  const [code, setCode] = useState("");
  const [verify, { isLoading, error }] = useVerifyTelegramCodeMutation();
  const secondsLeft = useCountdown(new Date(expiresAt).getTime());

  const submit = async () => {
    if (!code.trim()) return;
    try {
      const result = await verify({ challenge_id: challengeId, code }).unwrap();
      onSuccess(result);
    } catch {
      // error surfaced via mutation state below
    }
  };

  return {
    code,
    setCode,
    submit,
    isSubmitting: isLoading,
    errorMessage: getVerifyError(error),
    isExpired: secondsLeft <= 0 || getErrorStatusCode(error) === CODE_EXPIRED_STATUS,
    secondsLeft,
  };
};
